/**
 * Generator 1: Database schema — emit per-schema extract under admin/lib/db/<namespace>/
 */

import { isLayoutField } from '@core-engine/schema/constants.js'
import { flattenFields, getManyToManyFields } from '@core-engine/schema/field-helpers/index.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import { toDrizzleType } from '@core-engine/schema/type-mappers/index.js'
import type {
  GeneratedFile,
  GeneratorOptions,
  Schema,
  SchemaField
} from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { singularize, toCamelCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths.js'

function getFieldModifiers(field: SchemaField, needsSql: { value: boolean }): string {
  const modifiers: string[] = []

  if (field.primaryKey) {
    modifiers.push('.primaryKey()')
  }

  const isDateField = field.type === 'date' || field.type === 'timestamp'
  const hasValidDefault =
    field.default !== undefined && field.default !== null && !(isDateField && field.default === '')

  if (hasValidDefault) {
    if (field.type === 'select' && field.multiple) {
      const defaultValues = Array.isArray(field.default) ? field.default : [field.default]
      const jsonDefault = JSON.stringify(defaultValues.map((value) => String(value))).replaceAll(
        "'",
        "''"
      )
      modifiers.push(`.default(sql\`'${jsonDefault}'::jsonb\`)`)
      needsSql.value = true
    } else if (typeof field.default === 'string') {
      modifiers.push(`.default('${field.default}')`)
    } else {
      modifiers.push(`.default(${field.default})`)
    }
  }

  if (field.name === 'createdAt' || field.name === 'updatedAt') {
    modifiers.push('.default(sql`CURRENT_TIMESTAMP`)')
    needsSql.value = true
  }

  if (field.required || field.primaryKey) {
    modifiers.push('.notNull()')
  }

  return modifiers.join('')
}

function generateTableDefinition(
  schema: Schema,
  requiredImports: Set<string>,
  needsSql: { value: boolean }
): string {
  const tableName = toPascalCase(schema.name)
  const variableName = toCamelCase(schema.name)
  const isSingle = schema.type === 'single'

  requiredImports.add('pgTable')

  const dbFields = flattenFields(schema.fields).filter(
    (field) =>
      !(field.type === 'relationship' && field.multiple === true) && !isLayoutField(field.type)
  )

  const hasDraftMode = schema.actions?.draft === true
  const hasPublishedField = dbFields.some((field) => field.name === 'published')

  const fieldDefs = dbFields
    .map((field) => {
      const drizzleType = toDrizzleType(field, requiredImports)
      const modifiers = getFieldModifiers(field, needsSql)
      const reference =
        field.type === 'relationship' && isUserRelationshipTarget(field.relationship)
          ? ".references(() => user.id, { onDelete: 'set null' })"
          : ''
      const line = `    ${field.name}: ${drizzleType}${reference}${modifiers}`

      if ((field.type === 'richtext' || field.type === 'markdown') && field.output === 'html') {
        const snakeName = field.name.replace(/([A-Z])/g, '_$1').toLowerCase()
        requiredImports.add('text')
        return `${line},\n    ${field.name}Html: text('${snakeName}_html')`
      }

      return line
    })
    .join(',\n')

  let publishedField = ''
  if (hasDraftMode && !hasPublishedField) {
    requiredImports.add('boolean')
    publishedField = `,\n    published: boolean().notNull().default(false)`
  }

  const hasCreatedAt = dbFields.some((field) => field.name === 'createdAt')
  const hasUpdatedAt = dbFields.some((field) => field.name === 'updatedAt')
  const hasCreatedBy = dbFields.some((field) => field.name === 'createdBy')
  const hasUpdatedBy = dbFields.some((field) => field.name === 'updatedBy')
  let generatedFields = ''
  if (!hasCreatedAt || !hasUpdatedAt) {
    requiredImports.add('timestamp')
    needsSql.value = true
  }
  if (!hasCreatedAt) {
    generatedFields += `,\n    createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull()`
  }
  if (!hasUpdatedAt) {
    generatedFields += `,\n    updatedAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull()`
  }
  if (!hasCreatedBy || !hasUpdatedBy) {
    requiredImports.add('text')
  }
  if (!hasCreatedBy) {
    generatedFields += `,\n    createdBy: text().references(() => user.id, { onDelete: 'set null' })`
  }
  if (!hasUpdatedBy) {
    generatedFields += `,\n    updatedBy: text().references(() => user.id, { onDelete: 'set null' })`
  }

  const hasSortOrder = dbFields.some((field) => field.name === 'sortOrder')
  let sortOrderField = ''
  if (!isSingle && !hasSortOrder) {
    requiredImports.add('integer')
    sortOrderField = `,\n    sortOrder: integer().notNull().default(0)`
  }
  const hasSlugField = !isSingle && dbFields.some((field) => field.name === 'slug')
  const indexes = hasSlugField
    ? `,\n  (table) => [\n    uniqueIndex('${tableName}_slug_key').on(table.slug)\n  ]`
    : ''
  if (hasSlugField) {
    requiredImports.add('uniqueIndex')
  }

  return `export const ${variableName} = pgTable(
  '${tableName}',
  {
${fieldDefs}${publishedField}${generatedFields}${sortOrderField}
  }${indexes}
)
`
}

function getCreatableSelectFields(schema: Schema): SchemaField[] {
  return flattenFields(schema.fields).filter(
    (field) => field.type === 'select' && field.creatable === true && !isLayoutField(field.type)
  )
}

function generateSelectOptionsTable(
  schema: Schema,
  requiredImports: Set<string>,
  needsSql: { value: boolean }
): string | null {
  const creatableSelectFields = getCreatableSelectFields(schema)
  if (creatableSelectFields.length === 0) {
    return null
  }

  const tableName = `${toPascalCase(schema.name)}SelectOptions`
  const variableName = `${toCamelCase(schema.name)}SelectOptions`
  const indexName = `${tableName}_fieldName_value_key`

  requiredImports.add('pgTable')
  requiredImports.add('text')
  requiredImports.add('timestamp')
  requiredImports.add('uniqueIndex')
  requiredImports.add('uuid')
  requiredImports.add('varchar')
  needsSql.value = true

  return `export const ${variableName} = pgTable(
  '${tableName}',
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    fieldName: varchar('fieldName', { length: 100 }).notNull(),
    label: varchar('label', { length: 255 }).notNull(),
    value: varchar('value', { length: 255 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'string' })
      .default(sql\`CURRENT_TIMESTAMP\`)
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'string' })
      .default(sql\`CURRENT_TIMESTAMP\`)
      .notNull(),
    createdBy: text('createdBy').references(() => user.id, { onDelete: 'set null' }),
    updatedBy: text('updatedBy').references(() => user.id, { onDelete: 'set null' })
  },
  (table) => [
    uniqueIndex('${indexName}').using(
      'btree',
      table.fieldName.asc().nullsLast().op('text_ops'),
      table.value.asc().nullsLast().op('text_ops')
    )
  ]
)
`
}

function schemaHasReverseManyToMany(targetSchema: Schema, currentSchemaName: string): boolean {
  return getManyToManyFields(targetSchema.fields).some(
    (field) => field.relationship === currentSchemaName
  )
}

function shouldEmitJunction(
  currentSchemaName: string,
  field: SchemaField,
  relatedSchemas: Map<string, Schema>
): boolean {
  const relationship = field.relationship
  if (!relationship) {
    return false
  }

  if (isUserRelationshipTarget(relationship)) {
    return true
  }

  const targetSchema = relatedSchemas.get(relationship)
  if (!targetSchema) {
    throw new Error(
      `Missing relationship schema "${relationship}" referenced by "${currentSchemaName}".`
    )
  }

  const hasReverse = schemaHasReverseManyToMany(targetSchema, currentSchemaName)
  if (!hasReverse) {
    return true
  }

  return currentSchemaName.localeCompare(relationship) <= 0
}

function generateJunctionTable(
  schemaName: string,
  field: SchemaField,
  requiredImports: Set<string>,
  siblingImports: Set<string>,
  currentNamespace: string
): { name: string; content: string } {
  const relationship = field.relationship
  if (!relationship) {
    throw new Error(`Relationship field "${field.name}" is missing "relationship".`)
  }

  const singularSchema = singularize(schemaName)
  const singularRelation = singularize(relationship)
  const junctionName = `${singularSchema}${toPascalCase(relationship)}`
  const junctionPascal = toPascalCase(junctionName)
  const schemaIdCol = `${singularSchema}Id`
  const relationIdCol = `${singularRelation}Id`
  const schemaTableRef = toCamelCase(schemaName)
  const relationTableRef = isUserRelationshipTarget(relationship)
    ? 'user'
    : toCamelCase(relationship)
  const relationIdType = isUserRelationshipTarget(relationship) ? 'text' : 'uuid'

  requiredImports.add('pgTable')
  requiredImports.add('uuid')
  if (relationIdType === 'text') {
    requiredImports.add('text')
  }
  requiredImports.add('primaryKey')
  if (!isUserRelationshipTarget(relationship)) {
    const relationshipNamespace =
      currentNamespace === schemaName ? resolveExtensionNamespace(relationship) : currentNamespace
    const relationshipImport =
      relationshipNamespace === currentNamespace
        ? `./${relationship}`
        : `../${relationshipNamespace}/${relationship}`
    siblingImports.add(`import { ${relationTableRef} } from '${relationshipImport}'`)
  }

  return {
    name: toCamelCase(junctionName),
    content: `export const ${toCamelCase(junctionName)} = pgTable(
  '${junctionPascal}',
  {
    ${schemaIdCol}: uuid().notNull().references(() => ${schemaTableRef}.id, { onDelete: 'cascade' }),
    ${relationIdCol}: ${relationIdType}().notNull().references(() => ${relationTableRef}.id, { onDelete: 'cascade' })
  },
  (table) => [primaryKey({ columns: [table.${schemaIdCol}, table.${relationIdCol}] })]
)
`
  }
}

export interface DatabaseGeneratorResult {
  files: GeneratedFile[]
  tableName: string
  junctionTables: string[]
}

export function generateDatabase(
  schema: Schema,
  dbDir: string,
  options: GeneratorOptions = {},
  relatedSchemas: Map<string, Schema> = new Map()
): DatabaseGeneratorResult {
  const outputNamespace = resolveExtensionNamespace(schema.name, options.outputNamespace)
  const requiredImports = new Set<string>()
  const siblingImports = new Set<string>()
  const needsSql = { value: false }

  const tableDef = generateTableDefinition(schema, requiredImports, needsSql)
  const selectOptionsTableDef = generateSelectOptionsTable(schema, requiredImports, needsSql)

  const emittedJunctions = getManyToManyFields(schema.fields)
    .filter((field) => shouldEmitJunction(schema.name, field, relatedSchemas))
    .map((field) =>
      generateJunctionTable(schema.name, field, requiredImports, siblingImports, outputNamespace)
    )

  const importBlocks: string[] = []
  if (needsSql.value) {
    importBlocks.push(`import { sql } from 'drizzle-orm'`)
  }

  const pgCoreImports = Array.from(requiredImports).sort()
  if (pgCoreImports.length > 0) {
    importBlocks.push(`import {
  ${pgCoreImports.join(',\n  ')}
} from 'drizzle-orm/pg-core'`)
  }

  if (siblingImports.size > 0) {
    importBlocks.push(...Array.from(siblingImports).sort())
  }

  importBlocks.push(
    outputNamespace === 'core'
      ? `import { user } from './schema'`
      : `import { user } from '../core/schema'`
  )

  const sections = [
    importBlocks.join('\n'),
    tableDef.trim(),
    selectOptionsTableDef?.trim(),
    ...emittedJunctions.map((junction) => junction.content.trim())
  ].filter(Boolean)

  const content = `${sections.join('\n\n')}\n`
  const tableName = toCamelCase(schema.name)

  return {
    files: [createGeneratedFile(`${dbDir}/${outputNamespace}/${schema.name}.ts`, content)],
    tableName,
    junctionTables: emittedJunctions.map((junction) => junction.name)
  }
}
